import { type FC, useCallback, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { TableCell, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Combobox, type ComboboxOption } from './ui/combobox';
import { MultiSelectCombobox } from './ui/multi-select-combobox';
import { X } from 'lucide-react';
import { useUniqueValuesInDimension } from '../services/reportBuilder.service';
import { defaultComparatorOptions, type AnalysisDimensionFilter, type ComparatorOption } from '../utils/filterComparators';

interface DimensionFilterRowProps {
  dimensionFilter: AnalysisDimensionFilter;
  onChange: (newFilter: AnalysisDimensionFilter) => void;
  onDelete: () => void;
  dimensionOptions: ComboboxOption[];
  comparatorOptions?: ComparatorOption[];
}

export const DimensionFilterRow: FC<DimensionFilterRowProps> = ({
  dimensionFilter,
  onChange,
  onDelete,
  dimensionOptions,
  comparatorOptions = defaultComparatorOptions,
}) => {
  const [query, setQuery] = useState<string>('');
  const [debouncedQuery] = useDebounce(query, 500);

  const { data: dimensionValues, isLoading } = useUniqueValuesInDimension(
    dimensionFilter.dimension_id || null,
    debouncedQuery
  );

  const comparator = dimensionFilter.comparator || 'IN';
  const supportsMultipleValues = comparator === 'IN';
  const comparatorsWithoutValues = useMemo(
    () => ['EMPTY', 'NOT_EMPTY', 'VALID_DATE', 'INVALID_DATE', 'VALID_NUMBER', 'INVALID_NUMBER'],
    []
  );
  const requiresValue = !comparatorsWithoutValues.includes(comparator);

  const valueOptions = useMemo(() => {
    if (!dimensionValues) return [];
    return dimensionValues.map(v => ({
      value: v,
      label: v.length > 50 ? `${v.slice(0, 50)}...` : v,
    }));
  }, [dimensionValues]);

  const selectedValues = useMemo(() => {
    if (!dimensionFilter.value) return [];
    if (Array.isArray(dimensionFilter.value)) {
      return dimensionFilter.value;
    }
    return [dimensionFilter.value];
  }, [dimensionFilter.value]);

  const valueForInput = useMemo(() => {
    if (!requiresValue || supportsMultipleValues) {
      return '';
    }
    if (!dimensionFilter.value) {
      return '';
    }
    if (Array.isArray(dimensionFilter.value)) {
      return dimensionFilter.value.length ? String(dimensionFilter.value[0]) : '';
    }
    return String(dimensionFilter.value);
  }, [dimensionFilter.value, requiresValue, supportsMultipleValues]);

  const handleComparatorChange = useCallback(
    (newComparator: string) => {
      const noValueComparators = new Set(comparatorsWithoutValues);
      const multiValueComparators = new Set(['IN']);

      let nextValue: string | string[] | undefined;
      if (multiValueComparators.has(newComparator)) {
        const currentValue = dimensionFilter.value;
        if (Array.isArray(currentValue)) {
          nextValue = currentValue;
        } else if (typeof currentValue === 'string' && currentValue.length) {
          nextValue = [currentValue];
        } else {
          nextValue = [];
        }
      } else if (noValueComparators.has(newComparator)) {
        nextValue = undefined;
      } else {
        const currentValue = dimensionFilter.value;
        if (Array.isArray(currentValue)) {
          nextValue = currentValue.length ? String(currentValue[0]) : '';
        } else if (typeof currentValue === 'string') {
          nextValue = currentValue;
        } else if (currentValue != null) {
          nextValue = String(currentValue);
        } else {
          nextValue = '';
        }
      }

      onChange({
        ...dimensionFilter,
        comparator: newComparator,
        value: nextValue,
      });
    },
    [comparatorsWithoutValues, dimensionFilter, onChange]
  );

  return (
    <TableRow>
      <TableCell>
        <Combobox
          options={dimensionOptions}
          value={dimensionFilter.dimension_id || ''}
          onValueChange={(newVal) => {
            onChange({
              ...dimensionFilter,
              dimension_id: newVal,
              comparator: 'IN',
              value: [],
            });
          }}
          placeholder="Select dimension..."
          emptyText="No dimensions available."
        />
      </TableCell>
      <TableCell>
        <Select value={comparator} onValueChange={handleComparatorChange}>
          <SelectTrigger>
            <SelectValue placeholder="Select comparator..." />
          </SelectTrigger>
          <SelectContent>
            {comparatorOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          {!requiresValue && (
            <span className="px-2 py-1 text-xs rounded-md bg-warning/20 text-warning-foreground">
              {comparator === 'EMPTY'
                ? 'Empty'
                : comparator === 'NOT_EMPTY'
                ? 'Not Empty'
                : comparator.replace('_', ' ')}
            </span>
          )}
          {requiresValue && supportsMultipleValues && (
            <MultiSelectCombobox
              options={valueOptions}
              value={selectedValues}
              onValueChange={(values) => {
                onChange({
                  ...dimensionFilter,
                  value: values,
                  comparator: 'IN',
                });
              }}
              placeholder="Select values..."
              emptyText="No values available."
              onInputChange={setQuery}
              isLoading={isLoading}
              className="flex-1"
            />
          )}
          {requiresValue && !supportsMultipleValues && (
            <Input
              type="text"
              value={valueForInput}
              onChange={(e) => {
                onChange({
                  ...dimensionFilter,
                  value: e.target.value,
                });
              }}
              placeholder="Enter value..."
              className="flex-1"
            />
          )}
        </div>
      </TableCell>
      <TableCell className="text-right">
        <Button variant="ghost" size="sm" onClick={onDelete} className="h-8 w-8 p-0">
          <X className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
};

